import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, List, X, Check, Rocket, TrendUp, Crown,
} from 'phosphor-react';
import { LogoWithText } from '../components/Logo';
import useMetaTags from '../hooks/useMetaTags';
import './LandingPage.css';
import './PricingPage.css';

const CORE_MODULES = [
  'Academics',
  'Attendance',
  'Finance & Accounting',
  'Communication',
  'Student Safety',
];

const PLANS = [
  {
    key: 'starter',
    name: 'Starter',
    icon: Rocket,
    priceLine: 'Per-student pricing, billed termly or monthly',
    tagline: 'Everything a school needs to run day to day.',
    features: CORE_MODULES,
    highlight: false,
  },
  {
    key: 'growth',
    name: 'Growth',
    icon: TrendUp,
    priceLine: 'Per-student pricing, billed termly or monthly',
    tagline: 'Everything in Starter, plus the tools to connect Campusio to the rest of your stack.',
    features: [...CORE_MODULES, 'Integrations & API access (webhooks, third-party sync)'],
    highlightFeatureIndex: CORE_MODULES.length,
    highlight: true,
    badge: 'Most Popular',
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    icon: Crown,
    priceLine: 'Per-student pricing, billed termly or monthly',
    tagline: 'Everything in Growth, plus dedicated support as you scale.',
    features: [...CORE_MODULES, 'Integrations & API access', 'Priority support', 'Custom onboarding', 'Multi-campus rollout'],
    highlight: false,
  },
];

function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('in-view');
      }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );

    document.querySelectorAll('.lp-fade,.lp-fade-left,.lp-fade-right')
      .forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);
}

export default function PricingPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useScrollReveal();

  useMetaTags({
    title: 'Pricing — Campusio',
    description: 'Simple, per-student pricing quoted for your school. See what\'s included in Starter, Growth, and Enterprise plans.',
  });

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <div style={{ background: 'var(--land-white)', color: 'var(--land-text)', fontFamily: "'IBM Plex Sans', sans-serif" }}>

      {/* ── Nav ─────────────────────────────────────────────────────── */}
      <nav className={`lp-nav${scrolled ? ' scrolled' : ''}`}>
        <div className="lp-nav-inner">
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <LogoWithText size={30} />
          </button>

          <div className="lp-nav-links">
            <button className="lp-nav-link" onClick={() => navigate('/features')}>Features</button>
            <button className="lp-nav-link" onClick={() => navigate('/pricing')}>Pricing</button>
            <button className="lp-nav-link" onClick={() => navigate('/documentation')}>Docs</button>
            <button className="lp-nav-link" onClick={() => navigate('/contact')}>Contact</button>
          </div>

          <div className="lp-nav-actions">
            <button className="lp-btn-ghost" onClick={() => navigate('/login')}>Sign In</button>
            <button className="lp-btn-primary" onClick={() => navigate('/contact')}>
              Request a Demo <ArrowRight size={15} weight="bold" />
            </button>
          </div>

          <button
            onClick={() => setMenuOpen(true)}
            style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
            className="lp-hamburger"
          >
            <List size={26} color="var(--land-text)" />
          </button>
        </div>
      </nav>

      {/* ── Mobile menu ──────────────────────────────────────────────── */}
      {menuOpen && (
        <div className="lp-mobile-menu">
          <button className="lp-mobile-close" onClick={() => setMenuOpen(false)}>
            <X size={20} />
          </button>
          <button className="lp-mobile-link" onClick={() => { navigate('/features'); setMenuOpen(false); }}>Features</button>
          <button className="lp-mobile-link" onClick={() => { navigate('/pricing'); setMenuOpen(false); }}>Pricing</button>
          <button className="lp-mobile-link" onClick={() => { navigate('/documentation'); setMenuOpen(false); }}>Documentation</button>
          <button className="lp-mobile-link" onClick={() => { navigate('/contact'); setMenuOpen(false); }}>Contact</button>
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 24, borderTop: '1px solid var(--land-border)' }}>
            <button className="lp-btn-outline-lg" style={{ justifyContent: 'center' }} onClick={() => { navigate('/login'); setMenuOpen(false); }}>Sign In</button>
            <button className="lp-btn-primary-lg" style={{ justifyContent: 'center' }} onClick={() => { navigate('/contact'); setMenuOpen(false); }}>Request a Demo</button>
          </div>
        </div>
      )}

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="pp-hero">
        <p className="lp-section-label lp-fade">Pricing</p>
        <h1 className="pp-hero-h1 lp-fade lp-delay-1">Simple pricing, quoted for your school.</h1>
        <p className="pp-hero-sub lp-fade lp-delay-2">
          Campusio is priced per student, not per feature. Every plan gets the same core platform —
          higher tiers unlock more as your school's needs grow.
        </p>
      </section>

      {/* ── Pricing Cards ────────────────────────────────────────────── */}
      <section style={{ background: 'var(--land-off)' }}>
        <div className="lp-section" style={{ paddingTop: 0 }}>
          <div className="pp-plans-grid">
            {PLANS.map((plan, i) => {
              const Icon = plan.icon;
              return (
                <div
                  key={plan.key}
                  className={`pp-plan-card lp-fade lp-delay-${i + 1}${plan.highlight ? ' pp-plan-card--highlight' : ''}`}
                >
                  {plan.badge && <div className="pp-plan-badge">{plan.badge}</div>}
                  <div className="pp-plan-icon"><Icon size={24} weight="duotone" /></div>
                  <h3 className="pp-plan-name">{plan.name}</h3>
                  <p className="pp-plan-tagline">{plan.tagline}</p>
                  <p className="pp-plan-price">{plan.priceLine}</p>

                  <ul className="pp-plan-features">
                    {plan.features.map((feature, idx) => (
                      <li
                        key={feature}
                        className={plan.highlightFeatureIndex === idx ? 'pp-plan-feature--standout' : ''}
                      >
                        <Check size={16} weight="bold" className="icon" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    className={plan.highlight ? 'lp-btn-primary-lg' : 'lp-btn-outline-lg'}
                    style={{ justifyContent: 'center', width: '100%' }}
                    onClick={() => navigate('/contact')}
                  >
                    Talk to us <ArrowRight size={16} weight="bold" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FAQ-style note ───────────────────────────────────────────── */}
      <section style={{ background: 'white' }}>
        <div className="lp-section" style={{ textAlign: 'center' }}>
          <p className="lp-section-label lp-fade">Good to know</p>
          <h2 className="lp-section-h2 lp-fade lp-delay-1" style={{ maxWidth: 620, margin: '0 auto 20px' }}>
            Pricing is per-student, and quoted per school.
          </h2>
          <p className="pp-note-body lp-fade lp-delay-2">
            Because every school's enrollment and billing cadence is different, we don't publish a flat
            dollar figure — each school gets a per-student rate agreed with our team, billed termly or
            monthly. This is a sales-assisted product: there's no self-serve checkout. Reach out and
            we'll put together a quote for your school within a day.
          </p>
          <div style={{ marginTop: 32 }} className="lp-fade">
            <button className="lp-btn-primary-lg" style={{ justifyContent: 'center' }} onClick={() => navigate('/contact')}>
              Talk to us <ArrowRight size={18} weight="bold" />
            </button>
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-footer-grid">
          <div>
            <div style={{ filter: 'brightness(0) invert(1)', opacity: 0.85 }}>
              <LogoWithText size={28} />
            </div>
            <p className="lp-footer-brand-desc">
              Africa's modern school management platform. Unified, reliable, and built to grow with your institution.
            </p>
          </div>
          <div>
            <div className="lp-footer-col-title">Platform</div>
            <ul className="lp-footer-links">
              <li><button onClick={() => navigate('/features')}>Features</button></li>
              <li><button onClick={() => navigate('/pricing')}>Pricing</button></li>
              <li><button onClick={() => navigate('/documentation')}>Documentation</button></li>
              <li><button onClick={() => navigate('/contact')}>Request a Demo</button></li>
            </ul>
          </div>
          <div>
            <div className="lp-footer-col-title">Company</div>
            <ul className="lp-footer-links">
              <li><button onClick={() => navigate('/about')}>About Us</button></li>
              <li><button onClick={() => navigate('/contact')}>Contact Us</button></li>
              <li><a href="#">Blog</a></li>
              <li><a href="#">Careers</a></li>
            </ul>
          </div>
          <div>
            <div className="lp-footer-col-title">Legal</div>
            <ul className="lp-footer-links">
              <li><a href="#">Privacy Policy</a></li>
              <li><a href="#">Terms of Service</a></li>
              <li><a href="#">Security</a></li>
              <li><a href="#">Cookie Policy</a></li>
            </ul>
          </div>
        </div>
        <div className="lp-footer-bottom">
          <span className="lp-footer-copy">&copy; 2024 Campusio. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
